import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAdminCoupon, defaultValue } from 'app/shared/model/admin-coupon.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null as string | null,
  coupons: [] as readonly IAdminCoupon[],
  totalItems: 0,
  coupon: defaultValue,
  updating: false,
  updateSuccess: false,
};

export type AdminCouponsState = Readonly<typeof initialState>;

const adminUrl = 'api/admin/coupons';

interface FetchParams {
  page: number;
  size: number;
  sort: string;
  /** Optional RSQL filter (code/audience/active, built on the client). */
  query?: string;
}

// Timora backend exposes the admin coupon list as an RSQL filter endpoint returning a
// GenericPaginationResponse ({ data, pagination }) — same contract as businesses/user-management.
export const getCoupons = createAsyncThunk(
  'adminCoupons/fetch_coupons',
  async ({ page, size, sort, query }: FetchParams, { signal }) => {
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    // Stable PK tiebreaker so rows don't reshuffle across edits when the primary sort has ties.
    const requestUrl = `${adminUrl}/filter?page=${page}&size=${size}&sort=${sort}&sort=id,desc${q}`;
    return axios.get<{ data: IAdminCoupon[]; pagination: { totalItems: number } }>(requestUrl, { signal });
  },
  { serializeError: serializeAxiosError },
);

export const getCoupon = createAsyncThunk('adminCoupons/fetch_coupon', async (id: number) => axios.get<IAdminCoupon>(`${adminUrl}/${id}`), {
  serializeError: serializeAxiosError,
});

export const createCoupon = createAsyncThunk(
  'adminCoupons/create_coupon',
  async (coupon: IAdminCoupon, thunkAPI) => {
    const result = await axios.post<IAdminCoupon>(adminUrl, coupon);
    thunkAPI.dispatch(getCoupons({ page: 0, size: 20, sort: 'id,desc' }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateCoupon = createAsyncThunk(
  'adminCoupons/update_coupon',
  async (coupon: IAdminCoupon, thunkAPI) => {
    const result = await axios.put<IAdminCoupon>(`${adminUrl}/${coupon.id}`, coupon);
    thunkAPI.dispatch(getCoupons({ page: 0, size: 20, sort: 'id,desc' }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteCoupon = createAsyncThunk(
  'adminCoupons/delete_coupon',
  async (id: number, thunkAPI) => {
    const result = await axios.delete(`${adminUrl}/${id}`);
    thunkAPI.dispatch(getCoupons({ page: 0, size: 20, sort: 'id,desc' }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const AdminCouponsSlice = createSlice({
  name: 'adminCoupons',
  initialState: initialState as AdminCouponsState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = action.payload.data.data;
        state.totalItems = action.payload.data.pagination.totalItems;
      })
      .addCase(deleteCoupon.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.coupon = defaultValue;
      })
      .addMatcher(isFulfilled(getCoupon, createCoupon, updateCoupon), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.coupon = action.payload.data;
      })
      .addMatcher(isPending(getCoupons), state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getCoupon), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createCoupon, updateCoupon, deleteCoupon), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(getCoupons), (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.loading = false;
        state.errorMessage = action.error.message!;
      })
      .addMatcher(isRejected(getCoupon, createCoupon, updateCoupon, deleteCoupon), (state, action) => {
        state.loading = false;
        state.updating = false;
        state.updateSuccess = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export const { reset } = AdminCouponsSlice.actions;

export default AdminCouponsSlice.reducer;
